import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { AppState, Platform } from 'react-native';

import { supabase } from '@/lib/supabase';
import { genderedArticle, resolveDisplayName } from '@/lib/format';

Notifications.setNotificationHandler({
  handleNotification: async (notification) => {
    // Τα μηνύματα chat έχουν ήδη δικό τους in-app "floating" toast +
    // ήχο (μέσω realtime, βλ. IncomingMessageToast) όσο η εφαρμογή είναι
    // ανοιχτή — δεν χρειάζεται να εμφανίζεται ΚΑΙ το OS push banner από πάνω.
    const type = (notification.request.content.data as { type?: string } | undefined)?.type;
    const appIsForeground = AppState.currentState === 'active';
    if (type === 'agreement_message' && appIsForeground) {
      return {
        shouldShowBanner: false,
        shouldShowList: true,
        shouldPlaySound: false,
        shouldSetBadge: false,
      };
    }
    return {
      shouldShowBanner: true,
      shouldShowList: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    };
  },
});

function getProjectId(): string | undefined {
  const fromExtra = Constants.expoConfig?.extra?.eas?.projectId;
  if (typeof fromExtra === 'string') return fromExtra;
  const easConfig = (Constants as { easConfig?: { projectId?: string } }).easConfig;
  return easConfig?.projectId;
}

export async function registerForPushNotificationsAsync(): Promise<string | null> {
  if (Platform.OS === 'web') return null;
  if (!Device.isDevice) return null;

  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Προεπιλογή',
      importance: Notifications.AndroidImportance.DEFAULT,
      lightColor: '#1E3A8A',
    });
    // Ξεχωριστό κανάλι με δικό του ήχο για μηνύματα chat — στυλ Viber, ώστε να
    // ξεχωρίζει ηχητικά από τις υπόλοιπες ειδοποιήσεις.
    await Notifications.setNotificationChannelAsync('chat-messages', {
      name: 'Μηνύματα συμφωνιών',
      importance: Notifications.AndroidImportance.HIGH,
      lightColor: '#0BC5C0',
      sound: 'pop.wav',
    });
  }

  const { status: existing } = await Notifications.getPermissionsAsync();
  let status = existing;
  if (existing !== 'granted') {
    status = (await Notifications.requestPermissionsAsync()).status;
  }
  if (status !== 'granted') return null;

  try {
    const projectId = getProjectId();
    const { data } = await Notifications.getExpoPushTokenAsync(
      projectId ? { projectId } : undefined,
    );
    return data;
  } catch {
    return null;
  }
}

let cachedToken: string | null = null;

export async function saveDeviceToken(userId: string, token: string): Promise<void> {
  cachedToken = token;
  await supabase.from('device_tokens').upsert(
    { token, user_id: userId, platform: Platform.OS },
    { onConflict: 'token' },
  );
}

export async function removeDeviceToken(token: string): Promise<void> {
  await supabase.from('device_tokens').delete().eq('token', token);
}

export async function unregisterCurrentDevice(): Promise<void> {
  if (!cachedToken) return;
  try {
    await removeDeviceToken(cachedToken);
  } catch {
    // αγνόησε — δεν θέλουμε να μπλοκάρει το logout
  }
  cachedToken = null;
}

export type AppNotification = {
  id: string;
  type: string;
  title: string;
  body: string;
  timestamp: string;
  route: string;
  params?: Record<string, string>;
  isEvent?: boolean; // true για notification_events rows
  eventId?: string;  // το πραγματικό uuid από notification_events
  readAt?: string | null;
};

/** Σημειώνει όλα τα notifications ως αναγνωσμένα (DB). */
export async function markAllNotificationsRead(): Promise<void> {
  await supabase.rpc('mark_all_notifications_read');
}

/** Σημειώνει ένα συγκεκριμένο notification_event ως αναγνωσμένο. */
export async function markNotificationRead(eventId: string): Promise<void> {
  await supabase
    .from('notification_events')
    .update({ read_at: new Date().toISOString() })
    .eq('id', eventId)
    .is('read_at', null);
}

/** Σημειώνει ως αναγνωσμένα όλα τα unread events για ένα συγκεκριμένο entity (π.χ. agreement/offer id). */
export async function markNotificationsReadForEntity(entityId: string): Promise<void> {
  await supabase
    .from('notification_events')
    .update({ read_at: new Date().toISOString() })
    .eq('related_entity_id', entityId)
    .is('read_at', null);
}

/** Σημειώνει ως αναγνωσμένα όλα τα unread events για ένα συγκεκριμένο route (π.χ. circle). */
export async function markNotificationsReadForRoute(route: string): Promise<void> {
  await supabase
    .from('notification_events')
    .update({ read_at: new Date().toISOString() })
    .eq('route', route)
    .is('read_at', null);
}

/** Παράγει όλες τις ειδοποιήσεις για το bell panel. */
export async function fetchMyNotifications(userId: string): Promise<AppNotification[]> {
  const items: AppNotification[] = [];

  // Οι 4 πηγές είναι εντελώς ανεξάρτητες μεταξύ τους — έτρεχαν η μία μετά
  // την άλλη (4 διαδοχικά round-trips), κάτι που εξηγούσε μεγάλο μέρος της
  // αργής φόρτωσης του κουδουνιού/chat panel. Τώρα φεύγουν παράλληλα.
  type ConnectionRow = { id: string; requester_id: string; profiles: { full_name: string | null; nickname: string | null; contact_email: string | null; user_number: number | null } | null; created_at: string };
  type OfferBellRow = { id: string; title: string; creator_id: string; creator_name: string; created_at: string };
  type DisputeRow = { id: string; agreement_id: string; agreements: { title: string; creator_id: string; counterparty_id: string } | null; dispute_statements: { user_id: string }[]; updated_at: string };

  const [
    { data: agreements },
    { data: connections },
    { data: pendingOffers },
    { data: disputes },
  ] = await Promise.all([
    supabase
      .from('agreements')
      .select('id, title, status, creator_id, counterparty_id, creator_agreed, counterparty_agreed, cancel_requested_by, completion_requested_by, completion_confirmed_at, payment_declared_by, payment_confirmed_at, updated_at')
      .or(`creator_id.eq.${userId},counterparty_id.eq.${userId}`)
      .not('status', 'in', '("draft","completed","terminated","cancelled","cancelled_mutual","cancelled_unilateral","withdrawn")')
      .order('updated_at', { ascending: false })
      .limit(50),
    supabase
      .from('connections')
      .select('id, requester_id, profiles!requester_id(full_name, nickname, contact_email, user_number), created_at')
      .eq('addressee_id', userId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(10) as unknown as Promise<{ data: ConnectionRow[] | null; error: unknown }>,
    supabase.rpc('get_pending_offers_for_bell') as unknown as Promise<{ data: OfferBellRow[] | null; error: unknown }>,
    supabase
      .from('disputes')
      .select('id, agreement_id, agreements(title, creator_id, counterparty_id), dispute_statements(user_id), updated_at')
      .eq('status', 'open')
      .order('updated_at', { ascending: false })
      .limit(10) as unknown as Promise<{ data: DisputeRow[] | null; error: unknown }>,
  ]);

  for (const a of agreements ?? []) {
    const isCreator = a.creator_id === userId;
    const myAgreed = isCreator ? a.creator_agreed : a.counterparty_agreed;

    if (a.status === 'pending_completion' && a.completion_requested_by && a.completion_requested_by !== userId) {
      items.push({
        id: `completion-req-${a.id}`,
        type: 'completion_requested',
        title: '🏁 Αίτημα ολοκλήρωσης',
        body: `«${a.title}» — επιβεβαιώνεις;`,
        timestamp: a.updated_at,
        route: '/(app)/agreements/[id]',
        params: { id: a.id },
      });
    }

    if (a.status === 'pending' && !myAgreed) {
      items.push({
        id: `pending-${a.id}`,
        type: 'agreement_pending',
        title: '✍️ Εκκρεμεί η υπογραφή σου',
        body: `«${a.title}»`,
        timestamp: a.updated_at,
        route: '/(app)/agreements/[id]',
        params: { id: a.id },
      });
    }

    if (a.status === 'cancel_requested' && a.cancel_requested_by !== userId) {
      items.push({
        id: `cancel-${a.id}`,
        type: 'cancel_requested',
        title: '⚠️ Αίτηση ακύρωσης',
        body: `«${a.title}» — συμφωνείς;`,
        timestamp: a.updated_at,
        route: '/(app)/agreements/[id]',
        params: { id: a.id },
      });
    }

    if (
      a.status === 'active' &&
      a.payment_declared_by &&
      a.payment_declared_by !== userId &&
      !a.payment_confirmed_at
    ) {
      items.push({
        id: `payment-${a.id}`,
        type: 'payment_declared',
        title: '💰 Δήλωση εξόφλησης',
        body: `«${a.title}» — επιβεβαιώνεις παραλαβή;`,
        timestamp: a.updated_at,
        route: '/(app)/agreements/[id]',
        params: { id: a.id },
      });
    }
  }

  // ── Εκκρεμείς συνδέσεις (actionable) ──────────────────────────────────────
  for (const c of connections ?? []) {
    const name = resolveDisplayName(c.profiles);
    items.push({
      id: `conn-${c.id}`,
      type: 'connection_request',
      title: '🤝 Αίτημα σύνδεσης',
      body: `${genderedArticle(name)} ${name} σε πρόσθεσε στον κύκλο ${genderedArticle(name) === 'Η' ? 'της' : 'του'}`,
      timestamp: c.created_at,
      route: '/(app)/circle',
    });
  }

  // ── Εκκρεμείς προσφορές (actionable) ──────────────────────────────────────
  for (const o of pendingOffers ?? []) {
    items.push({
      id: `offer-${o.id}`,
      type: 'offer_pending',
      title: '📋 Νέα προσφορά προς εσένα',
      body: `${genderedArticle(o.creator_name)} ${o.creator_name} σου έστειλε προσφορά: «${o.title}»`,
      timestamp: o.created_at,
      route: '/(app)/offers/[id]',
      params: { id: o.id },
    });
  }

  // ── Ανοιχτές διαφορές (actionable) ────────────────────────────────────────
  for (const d of disputes ?? []) {
    const agr = d.agreements;
    if (!agr) continue;
    const isParty = agr.creator_id === userId || agr.counterparty_id === userId;
    if (!isParty) continue;
    const submitted = (d.dispute_statements ?? []).some((s) => s.user_id === userId);
    if (submitted) continue;
    items.push({
      id: `dispute-${d.id}`,
      type: 'dispute_open',
      title: '🚨 Ανοιχτή διαφορά',
      body: `«${agr.title}» — υπόβαλε τη δήλωσή σου`,
      timestamp: d.updated_at,
      route: '/(app)/agreements/[id]',
      params: { id: d.agreement_id },
    });
  }

  items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return items;
}

const RECENT_EVENTS_PAGE_SIZE = 6;

/**
 * Φορτώνει μία «σελίδα» από τα notification_events (τα πραγματικά bell items,
 * με read_at) — ξεχωριστά από τα «actionable» items του fetchMyNotifications.
 * Πάντα ΟΛΑ τα events (αναγνωσμένα και μη), νεότερα προς παλαιότερα — το
 * `before` cursor επιτρέπει «Φόρτωση παλιότερων» χωρίς να φορτώνεται άσκοπα
 * όλο το ιστορικό σε κάθε άνοιγμα του κουδουνιού.
 */
export async function fetchNotificationEventsPage(
  userId: string,
  opts: { before?: string },
): Promise<{ events: AppNotification[]; hasMore: boolean }> {
  const limit = RECENT_EVENTS_PAGE_SIZE;
  let q = supabase
    .from('notification_events')
    .select('id, type, title, body, route, related_entity_id, created_at, read_at')
    .eq('recipient_id', userId)
    // Τα μηνύματα chat έχουν το δικό τους εικονίδιο/ιστορικό πλέον — δεν
    // εμφανίζονται πια στο κουδούνι ειδοποιήσεων.
    .neq('type', 'agreement_message')
    .order('created_at', { ascending: false })
    .limit(limit + 1);
  if (opts.before) q = q.lt('created_at', opts.before);

  const { data } = await q;
  const rows = data ?? [];
  const hasMore = rows.length > limit;
  const page = rows.slice(0, limit);

  const events: AppNotification[] = page.map((e) => {
    const route = e.route ?? '/(app)';
    const params = (route.includes('[id]') || route === '/(app)/admin/feedback') && e.related_entity_id
      ? { id: e.related_entity_id }
      : undefined;
    return {
      id: `event-${e.id}`,
      eventId: e.id,
      type: e.type,
      title: e.title,
      body: e.body,
      timestamp: e.created_at,
      route,
      params,
      isEvent: true,
      readAt: e.read_at,
    };
  });

  return { events, hasMore };
}
